/**
 * Tappy, the help chat: the small pure rules both the composables and the tests share (Grant 2026-11-02).
 * Not the Setup assistant (utils/setupAssistant.js).
 */

export const TAPPY_NAME = 'Tappy';

/** English fallbacks for the greeting strings the API serves per language (TappyGreetings in SLL.Api). */
export const TAPPY_FALLBACK = Object.freeze({
  intro: "Hi, I'm Tappy, the Tap Leagues AI helper. Ask how to do anything in Tap Leagues. I answer questions; I don't change your club.",
  aiLabel: 'AI helper',
  askLabel: 'Ask Tappy',
});

export const TAPPY_NOT_AVAILABLE = "Tappy isn't available for your account right now.";

/**
 * Whether this person may use Tappy. The SERVER's answer (`user.canUseAssistant`, computed per request from the
 * super admin audience switches) when it sent one; an older API build sends none, and then the old club-admin
 * rule stands. Never for anyone signed out.
 */
export function canUseTappy(user, isAuthenticated) {
  if (!isAuthenticated || !user) return false;
  if (typeof user.canUseAssistant === 'boolean') return user.canUseAssistant;
  return !!(user.superAdmin || (user.adminFor && user.adminFor.length > 0));
}

/** The notice shown when the API refuses with 403 notAvailable: the server's words, else the friendly line. */
export function tappyNotAvailableNotice(body) {
  return {
    code: body?.code || 'notAvailable',
    message: body?.global?.[0] || TAPPY_NOT_AVAILABLE,
  };
}

/** A refusal thrown by httpRequest is the parsed body ({global, code}); a raw fetch Response carries status. */
export function isTappyNotAvailable(error) {
  return !!error && (error.code === 'notAvailable' || error.status === 403);
}

/** The hand-over button's label: the club's name only when there is more than one to choose from. */
export function setupHandOverLabel(clubs, club) {
  return clubs.length > 1 ? `Open the Setup assistant · ${club.name}` : 'Open the Setup assistant';
}

/** The greeting with English where a string is missing (greeting still loading, or an older API). */
export function tappyWords(greeting) {
  return {
    intro: greeting?.intro || TAPPY_FALLBACK.intro,
    aiLabel: greeting?.aiLabel || TAPPY_FALLBACK.aiLabel,
    askLabel: greeting?.askLabel || TAPPY_FALLBACK.askLabel,
    anyLanguageLine: greeting?.anyLanguageLine || null,
  };
}

/*
 * Tappy's face and moves (Grant 2026-11-02, design TappyFace / Animations / Backgrounds). The avatar component
 * (components/assistant/TappyAvatar.vue) draws them; these are the rules the chat and the tests share.
 */

/** The circle Tappy sits on: the pink brand tint for now (one place to change when another plate is picked). */
export const TAPPY_PLATE = '#FBEDF0';

export const TAPPY_ANIMATIONS = Object.freeze(['idle', 'hello', 'bounce', 'peek', 'thinking', 'talking', 'wink']);

/**
 * How long each one-shot move plays before the avatar settles back to idle (the design's cycle lengths: Hello 6s,
 * Bounce 2.4s, Peek 5s, Wink 3.6s). Thinking and Talking loop while their state lasts, so they are not here.
 */
export const TAPPY_ONE_SHOT_MS = Object.freeze({hello: 6000, bounce: 2400, peek: 5000, wink: 3600});

/** The move actually played: a known one, or idle; always idle when the reader asked for reduced motion. */
export function tappyAnimation(animation, reducedMotion) {
  if (reducedMotion) return 'idle';
  return TAPPY_ANIMATIONS.includes(animation) ? animation : 'idle';
}

/**
 * The move for the small avatar beside one of Tappy's bubbles. The in-progress reply thinks until its first words
 * arrive and talks while they stream; the reply that just finished bounces once; a reply just rated up winks once;
 * every other bubble's avatar stays still. `finished` / `winking` are the message objects the chat is playing those
 * one-shots for (null when none).
 */
export function tappyMessageAnimation(message, {finished = null, winking = null} = {}) {
  if (!message || message.role !== 'assistant') return 'idle';
  if (message.pending) return message.text ? 'talking' : 'thinking';
  if (winking && winking === message) return 'wink';
  if (finished && finished === message) return 'bounce';
  return 'idle';
}
